import { action, runInAction, makeObservable } from "mobx";
import ImportacaoApi from "../api/ImportacaoApi";
import InformacoesProcessoApi from "../api/InformacoesProcessoApi";
import { UtilData } from "../misc/UtilData";
import { Processo } from "../modelo";
import { Peca } from "../modelo/dominio/Peca";
import { TipoPeca } from "../modelo/dominio/TipoPeca";
import { PecaDto } from "../modelo/dto/PecaDto";
import { ProcessoExterno } from "../modelo/ProcessoExterno";
import { ProcessoImportacao } from "../modelo/ProcessoImportacao";
import UsuarioStore from '../store/UsuarioStore';
import { AppStore } from "../store/AppStore";
import FiltroStore from "../store/FiltroStore";
import ImportacaoProcessosStore from "../store/ImportacaoProcessosStore";
import ProcessoStore from "../store/ProcessoStore";
import RouterStore from "../store/RouterStore";
import EscaninhoService from "./EscaninhoService";
import ImportacaoServiceReaction from "./ImportacaoServiceReaction";
import InformacoesProcessoService from "./InformacoesProcessoService";
import MapeadorProcesso from "./mapeadores/MapeadorProcesso";
import ProcessoService from "./ProcessoService";
import UtilNumeroProcesso from "../utils/UtilNumeroProcesso";

class ImportacaoService {
    evtImportacao;

    constructor() {
        makeObservable(this, {
            handleMudaAba: action,
            verificaProcessoNoEscaninhoEAdd: action,
            adicionaNumeroNoFiltro: action
        });
    }

    iniciaEventoImportacao(codImportacao: string) {
        if (!ImportacaoProcessosStore.iniciada || ImportacaoProcessosStore.codImportacao !== codImportacao) {
            ImportacaoProcessosStore.codImportacao = codImportacao;
            ImportacaoProcessosStore.iniciada = true;
            ImportacaoServiceReaction.iniciarReaction();
            const codSplit = codImportacao.split('-');
            runInAction(() => {
                ImportacaoProcessosStore.processosImportados = [];

                ImportacaoProcessosStore.inicializar();
                ImportacaoProcessosStore.totalDeProcessosParaImportar = parseInt(codSplit[codSplit.length - 1]);
            });
        }
    }

    getDadosImportacao(processos: ProcessoImportacao[]) {
        const procsParaAdicionar = processos.filter(processo => {
            const find = ImportacaoProcessosStore.processosImportados.find(proc => proc.numero === processo.numero);
            if (find != undefined && find.mensagem !== processo?.mensagem) {
                runInAction(() => {
                    find.mensagem = processo?.mensagem || "";
                });
            }
            return !find;
        });

        procsParaAdicionar.forEach(processo => {
            runInAction(() => {
                ImportacaoProcessosStore.processosImportados.push(processo);
            });
        });

    }

    finalizarEvtImportacao() {
        if (ImportacaoProcessosStore.processosImportados.length >= ImportacaoProcessosStore.totalDeProcessosParaImportar) {
            // this.evtImportacao?.close();
            ImportacaoProcessosStore.iniciada = false;
            ImportacaoProcessosStore.codImportacao = '';
            setTimeout(() => {
                FiltroStore.procurar(false);
            }, 10);
        }
        if (!AppStore.isCarregandoTodosProcessos) {
            /*let faltantes = ImportacaoProcessosStore.processosComSucesso.filter(procSucesso => !ProcessoStore._processos.find(proc => proc.numeroProcesso === procSucesso.numero));
            faltantes.forEach(async proc => {
                let idProcesso = await ProcessoService.idProcessoPorNumero(proc.numero);
                this.forcarCarregamentoDeProcesso(proc, idProcesso);
            });*/

        } else {
            setTimeout(() => {
                this.finalizarEvtImportacao();
            }, 100);
        }
        setTimeout(() => {
            FiltroStore.procurar(false);
        }, 0);
    }

    carregarProcessosParaImportar = async () => {
        runInAction(() => {
            ImportacaoProcessosStore.inicializar();
            ImportacaoProcessosStore.isCarregandoProcessos = true;
        });
        try {
            const processos = await ImportacaoApi.carregarProcessosParaImportarGraphql(UsuarioStore.gabinete);
            runInAction(() => {
                const processosMap = [];
                processos.forEach(proc => processosMap[proc.identificadorProcesso] = proc);
                ImportacaoProcessosStore.processosQuePodemSerImportados = Object.keys(processosMap).map(idProc => ProcessoExterno.novoProcessoExterno(processosMap[idProc]));
                ImportacaoProcessosStore.carregamentoProcessosConcluido = true;
            });
        } finally {
            runInAction(() => {
                ImportacaoProcessosStore.isCarregandoProcessos = false;
            });
        }
    }

    buscarProcessosImportaveisPorNumero = async (numerosProcessos: string[]): Promise<ProcessoExterno[]> => {
        const numerosUnicos = Array.from(new Set(numerosProcessos));
        const resultados = await Promise.allSettled(numerosUnicos.map(async numeroProcesso => {
            const idProcesso = await ProcessoService.idProcessoPorNumero(numeroProcesso);
            const dadosProcesso = await InformacoesProcessoService.consultarProcesso(
                idProcesso,
                false,
                { carregarPecas: false }
            );

            return { idProcesso, dadosProcesso };
        }));

        const processosLocalizados = resultados
            .filter((resultado): resultado is PromiseFulfilledResult<any> => resultado.status === 'fulfilled')
            .map(resultado => resultado.value)
            .filter(({ dadosProcesso }) =>
                dadosProcesso?.orgaoJulgador?.sigla === UsuarioStore.localizacao?.siglaOrgaoJulgador
            );

        if (processosLocalizados.length === 0) {
            return [];
        }

        const idsProcessos = processosLocalizados.map(({ idProcesso }) => idProcesso);
        const processosJaImportados = await ProcessoService.recuperaPorSearch({
            gabinete: UsuarioStore.gabinete,
            idsProcessos
        });
        const idsJaImportados = new Set(processosJaImportados.map(processo => Number(processo.identificadorProcesso)));

        return processosLocalizados
            .filter(({ idProcesso }) => !idsJaImportados.has(Number(idProcesso)))
            .map(({ dadosProcesso }) => {
                const processo = ProcessoExterno.novoProcessoExternoParaImportar(dadosProcesso);
                processo.tarefaFluxoPje = dadosProcesso.tarefaFluxoPje;
                return processo;
            });
    };

    extrairNumerosCompletos = (texto: string): string[] => {
        return Array.from(new Set(
            (texto || '')
                .split(/;|\n/)
                .map(numero => numero.trim())
                .filter(numero => {
                    const numeroSemClasse = numero.includes(' - ')
                        ? numero.split(' - ').slice(1).join(' - ').trim()
                        : numero;
                    return UtilNumeroProcesso.NUMERO_PROCESSO_PATTERN.test(numeroSemClasse)
                        || /^\d{20}$/.test(numeroSemClasse);
                })
                .map(numero => UtilNumeroProcesso.matchObjetoNumeroProcesso(numero).numeroCompleto)
        ));
    };

    handleMudaAba = (e: React.MouseEvent, novaAbaSelecionada: number) => {
        ImportacaoProcessosStore.abaSelecionada = novaAbaSelecionada;
    };

    importarTodosProcessos = async ( origemImportacaoGE: string, cpfServidor:string, numeroControleGab: number) => {
        const todosProcessos = ImportacaoProcessosStore.processosQuePodemSerImportadosFiltrado
            .map(processoExt => processoExt.numeroProcesso);
        await this.importarProcessosGe(todosProcessos,origemImportacaoGE, cpfServidor, numeroControleGab);
    }

    importarProcessos = async (origemImportacaoGE: string, cpfServidor:string, numeroControleGab: number) => {
        const processosFiltrados = ImportacaoProcessosStore.processosQuePodemSerImportados
            .filter(processoExt => processoExt.selecionado)
            .map(processoExt => processoExt.numeroProcesso);
        const importacaoIniciadaNoEspelho = origemImportacaoGE === 'Espelho do Processo';
        if (!importacaoIniciadaNoEspelho && !ProcessoStore.carregouProcessosNoEscaninho) {
            await EscaninhoService.carregarProcessosDoGabinete();
            // BTV desativado: os dados retornados não têm consumidores no frontend.
            // await InformacoesProcessoService.buscarDadosProcessoBTV(UsuarioStore.gabinete);
        }
        await this.importarProcessosGe(processosFiltrados, origemImportacaoGE, cpfServidor, numeroControleGab);
    }

    private importarProcessosGe = async (processos: string[], origemImportacaoGE: string, cpfServidor:string, numeroControleGab: number) => {
        const importacaoIniciadaNoEspelho = origemImportacaoGE === 'Espelho do Processo';
        runInAction(() => {
            ImportacaoProcessosStore.processosImportados = [];
            ImportacaoProcessosStore.processosNoEscaninho = [];
            ImportacaoProcessosStore.totalDeProcessosParaImportar = processos.length || 1;
        });
        const uuid = await ImportacaoApi.importarProcessos(processos, origemImportacaoGE, cpfServidor, numeroControleGab);
        const rotaEscaninho = '/escaninho/gerencial?codImportacao=' + uuid;

        if (importacaoIniciadaNoEspelho) {
            RouterStore.redirecionamentoExterno(rotaEscaninho);
            return;
        }

        RouterStore.push(rotaEscaninho);
    }

    async consultarDadosProcesso(processo: Processo) {
        await this.carregarPartes(processo);
        // await this.carregarDetalhesProcesso(processo);
        await this.carregarPecasProcesso(processo);
    }

    async carregarPartes(processo: Processo) {
        const partesProcesso = await InformacoesProcessoService.consultarPartesDaListaDeProcesso([processo.identificadorProcesso]);

        runInAction(() => {
            processo.partes = partesProcesso.get(processo.identificadorProcesso);
        });
    }

    async carregarDetalhesProcesso(processo: Processo) {
        const dadosProcesso = await ProcessoService.recuperaProcessoPorId(processo.identificadorProcesso);

        runInAction(() => {
            processo.classeJudicial = dadosProcesso.classesJudicial[0];
        });
    }

    async carregarPecasProcesso(processo: Processo) {
        const idProcesso = processo.identificadorProcesso;
        const docs = await InformacoesProcessoApi.consultarDocumentosProcesso(idProcesso);
        const peticoesPendentes = await InformacoesProcessoApi.consultarPeticoesPendentes(idProcesso);

        runInAction(() => {
            processo.pecas = docs.map((doc: PecaDto, index, array) => Peca.novo({
                ...doc,
                peticaoNaoLida: peticoesPendentes.some((pet: PecaDto) => pet.idUnicoDocumento === doc.idUnicoDocumento),
                numeroOrdem: array.length - index,
                juntadoEm: doc.juntadoEm && new Date(UtilData.formataData(doc.juntadoEm, true)),
                tipoPeca: doc.tipo && new TipoPeca(doc.tipo.nomeTipoProcessoDocumento)
            }));
        });
    }

    verificaProcessoNoEscaninhoEAdd = (processoImportacao: ProcessoImportacao) => {
        this.adicionaNumeroNoFiltro(processoImportacao);
        this.finalizarEvtImportacao();
        return;
    };

    forcarCarregamentoDeProcesso = async (processoImportacao: ProcessoImportacao, idProcesso: number) => {
        const resultadoConsulta = await ProcessoService.processoPorIdFiltroRapido(UsuarioStore.gabinete, idProcesso);
        if ((processoImportacao.mensagem.toLowerCase().includes('erro') && resultadoConsulta.length === 0)
            || !resultadoConsulta[0]) {
            runInAction(() => {
                processoImportacao.erro = true;
            });
            console.warn('Não foi possível localizar processo que deveria ter sido importado!');
            this.finalizarEvtImportacao();
            return;
        }
        let processo = Processo.novo(resultadoConsulta[0]);
        runInAction(() => {
            processo = MapeadorProcesso.toProcessos([processo])[0];
            //nova conferencia
            if (!ProcessoStore._processos.find(proc => proc.identificadorProcesso === processo.identificadorProcesso)) {
                ProcessoStore._processos.push(processo);
                processo.setBackground();
            }
        });
        await EscaninhoService.funcoesDeCarregamentoDeProcesso(processo);
    }
    adicionaNumeroNoFiltro = (ultimoProcessoImportado: ProcessoImportacao) => {
        const existeNoFiltro = FiltroStore.listaDeNumeroProcessoFilter.split(';').find(num => num === ultimoProcessoImportado.numero);
        if (!existeNoFiltro) {
            FiltroStore.listaDeNumeroProcessoFilter += FiltroStore.listaDeNumeroProcessoFilter.length === 0 ? '' : ';';
            FiltroStore.listaDeNumeroProcessoFilter += ultimoProcessoImportado.numero;
        }

        // Durante a importação, a lista de números deve exibir conjuntamente processos
        // dentro e fora do fluxo, sem ativar o filtro visível "somente fora do fluxo".
        FiltroStore.foraDoGabineteFilter = false;
        FiltroStore.incluirForaDoGabineteFilter = true;
    };


    async verificaProcessosImportados() {
        const processosImportados = ImportacaoProcessosStore.processosImportados;

        const ultimoProcessoImportado = processosImportados[processosImportados.length - 1];
        if (!ultimoProcessoImportado) {
            return;
        }

        if (ultimoProcessoImportado.erro) {
            console.info('não buscar processo com erro');
            this.finalizarEvtImportacao();
            return;
        }

        const numeroProcesso = ultimoProcessoImportado.numero.split(' - ');
        const idProcesso = await ProcessoService.idProcessoPorNumero(numeroProcesso.length === 2 ? numeroProcesso[1] : numeroProcesso[0]);
        const index = ProcessoStore._processos.findIndex(procStore => procStore.identificadorProcesso === idProcesso);
        if (index >= 0) {
            console.info('não buscar processo que já existe no escaninho');
            runInAction(() => {
                this.verificaProcessoNoEscaninhoEAdd(ultimoProcessoImportado);
            });
            return;
        }

        this.verificaProcessoNoEscaninhoEAdd(ultimoProcessoImportado);
    }

    verificaStatus = async (codImportacao: string) => {
        return ImportacaoApi.verificarProcessosImportados(codImportacao);
    }

    private reconstruirProgressoDosProcessosCarregados = () => {
        const normalizarNumero = (numero: string) => (numero || '').replace(/\D/g, '').slice(-20);
        const numerosNoEscaninho = new Set(
            ProcessoStore._processos.map(processo => normalizarNumero(processo.numeroProcesso))
        );

        ImportacaoProcessosStore.processosImportados.forEach(processo => {
            // No recarregamento da rota, os status são recuperados novamente, mas os eventos
            // que alimentaram processosNoEscaninho não são reenviados. Erros já são estados
            // finais; sucessos só contam quando o processo realmente está disponível na tela.
            if (processo.erro || numerosNoEscaninho.has(normalizarNumero(processo.numero))) {
                ImportacaoProcessosStore.adicionarProcessoNoEscaninho(processo);
            }
        });
    };

    verificarImportacao = () => {
        if (RouterStore.isImportacaoNoEscaninho) {

            const codImportacao = RouterStore.getParam('codImportacao');
            if (codImportacao !== undefined) {
                // let codSplit = codImportacao[0].split('-');
                // let totalDeProcessosParaSeremImportados = parseInt(codSplit[codSplit.length - 1]);
                this.verificaFimDeImportacao(codImportacao);
            }
        }
    }

    verificaFimDeImportacao = async (codImportacao: string[]) => {
        // let codSplit = codImportacao[0].split('-');
        // let totalDeProcessosParaSeremImportados = parseInt(codSplit[codSplit.length - 1]);

        const statusProcessos = await this.verificaStatus(codImportacao[0]);

        if (ImportacaoProcessosStore.progressoTotal <= 100) {
            const processosImportados = [];
            statusProcessos.forEach(statusProc => {
                if (statusProc.txtEstado.includes("Erro")) {
                    const jaVerificado = ImportacaoProcessosStore.processosComErro.some(proc => {
                        return proc.numero === statusProc.numeroProcesso;
                    });
                    if (!jaVerificado) {
                        const processoImportacao = new ProcessoImportacao(statusProc.txtEstado, true, statusProc.numeroProcesso);
                        processosImportados.push(processoImportacao);
                    }
                } else {
                    const jaVerificado = ImportacaoProcessosStore.processosComSucesso.some(proc => {
                        return proc.numero === statusProc.numeroProcesso;
                    });
                    if (!jaVerificado) {
                        const processoImportacao = new ProcessoImportacao(statusProc.txtEstado, false, statusProc.numeroProcesso);
                        processosImportados.push(processoImportacao);
                    }
                }
            });
            ImportacaoProcessosStore.adicionarProcessosImportados(processosImportados);
            this.reconstruirProgressoDosProcessosCarregados();
            if (RouterStore.isImportacaoNoEscaninho && ImportacaoProcessosStore.progressoTotal < 100) {
                setTimeout(() => {
                    this.verificaFimDeImportacao(codImportacao);
                }, 5000);
            }
        }
    }
}

export default new ImportacaoService();
